import { z } from 'zod';
import { MAX_COLOR_OPTIONS, type ProductMeasurementKind } from './catalog';
import { normalizedUuidSchema as uuidSchema } from './common';
import {
  collectDefinitionIssues,
  cylinderFrameWidthMm,
  cylinderVerticalExtent,
  doCylinderCurvesCross,
  findFlippedMeshCell,
  GEOMETRY_TOLERANCE,
  isAxisAlignedRect,
  isSimplePolygon,
  polygonArea,
  rectContainsOutline,
  type SizeMm,
} from './editor-geometry';
import {
  FACE_MASK_KEY_PATTERN,
  FACE_PHOTO_KEY_PATTERN,
  FACE_SAFE_AREA_KEY_PATTERN,
  FACE_SHADING_KEY_PATTERN,
  faceMaskKeySchema,
  facePhotoKeySchema,
  facePhotoSizeSchema,
  faceSafeAreaKeySchema,
  faceShadingKeySchema,
} from './image-asset';

export {
  ALPHA_IMAGE_CONTENT_TYPES,
  FACE_MASK_KEY_PATTERN,
  FACE_MASK_SEGMENT,
  FACE_PHOTO_KEY_PATTERN,
  FACE_PHOTO_SEGMENT,
  FACE_SAFE_AREA_KEY_PATTERN,
  FACE_SAFE_AREA_SEGMENT,
  FACE_SHADING_KEY_PATTERN,
  FACE_SHADING_SEGMENT,
  faceMaskKeySchema,
  facePhotoKeySchema,
  facePhotoSizeSchema,
  faceSafeAreaKeySchema,
  faceShadingKeySchema,
  MAX_PHOTO_PX,
  SAFE_AREA_IMAGE_CONTENT_TYPES,
} from './image-asset';

// ---------------------------------------------------------------------------
// editor 도메인 계약 — 편집영역 정의(EditAreaDefinition)와 편집영역 API 입출력.
//
// 정책 원본: docs/products/custom-policy.md §1·§2. 구현 계획: docs/design/custom/editor-api-contract-plan.md §5·§6.
// 저장 위치는 product_edit_areas.definition(jsonb) 이고, JSON 의 모양 라벨은 행의 schema_version 컬럼이다.
// JSON 안에 schemaVersion·coordinateSystem 을 두지 않는다 — 형식은 컬럼이, 좌표계는 아래 상수가 정한다.
// EDIT_AREA_SCHEMA_VERSION 은 기존 행을 무효로 만드는 변경일 때만 올린다. 서피스 유니온 멤버나 선택 필드를
// 더하는 변경은 1 을 유지한다(ADR-0007 D4 — cylinder·윤곽·합성 설정이 그 예다).
// ---------------------------------------------------------------------------

/** product_edit_areas.schema_version 에 저장하는 현재 형식 라벨. */
export const EDIT_AREA_SCHEMA_VERSION = 1;

/**
 * 좌표 3층. 저장 JSON 에는 이 선언이 없고 계약이 고정한다.
 * - 컴포넌트·디자인: 실측 mm, 원점 컴포넌트 좌상단, y 아래.
 * - 서피스 제어점: 면 사진 기준 %(0~100), 원점 사진 좌상단, y 아래. flat·cylinder 는 TL·TR·BR·BL 4점, mesh 는 row-major. 윤곽(outline)도 같은 좌표이며 찍은 순서대로 저장하고 첫 점을 반복하지 않는다.
 * - 배치 위치: 서피스 realSize mm 프레임, 원점 서피스 좌상단, y 아래.
 * 변환: 컴포넌트 안 점 → 서피스 mm(position + 점) → 정규화(÷ realSize) → flat 은 선형 보간, mesh 는 셀 bilinear,
 * cylinder 는 원통 투영(ADR-0007 부록 A) → 사진 px(% / 100 × photoSize). 축척(px/mm)은 저장하지 않고 이 변환에서 유도된다.
 */
export const EDIT_AREA_COORDINATE_SYSTEM = {
  component: { unit: 'mm', origin: 'top-left', yAxis: 'down' },
  surfacePoints: {
    unit: 'photo-percent',
    min: 0,
    max: 100,
    origin: 'top-left',
    yAxis: 'down',
    flatOrder: ['TL', 'TR', 'BR', 'BL'],
    meshOrder: 'row-major-from-top-left',
    cylinderOrder: ['TL', 'TR', 'BR', 'BL'],
    outlineOrder: 'as-drawn-implicitly-closed',
  },
  shapePoints: { unit: 'mm', origin: 'component-top-left', order: 'as-drawn-implicitly-closed' },
  placementPosition: { unit: 'mm', frame: 'surface.realSize', origin: 'top-left', yAxis: 'down' },
} as const;

export const FACE_KEYS = ['front', 'back', 'left', 'right'] as const;
export type FaceKey = (typeof FACE_KEYS)[number];
/** 앞면만 필수, 뒤·좌·우는 선택(정책 §1) — 한 면만 인쇄하는 상품은 뒷면 사진 없이 둘 수 있다. */
export const REQUIRED_FACE_KEYS = ['front'] as const;
export type RequiredFaceKey = (typeof REQUIRED_FACE_KEYS)[number];
export const OPTIONAL_FACE_KEYS = ['back', 'left', 'right'] as const;
export type OptionalFaceKey = (typeof OPTIONAL_FACE_KEYS)[number];
export const isRequiredFace = (face: FaceKey): face is RequiredFaceKey =>
  REQUIRED_FACE_KEYS.some((key) => key === face);

export const SURFACE_TYPES = ['flat', 'mesh', 'cylinder'] as const;
export type SurfaceType = (typeof SURFACE_TYPES)[number];

interface SurfaceKindRule {
  readonly allowed: readonly SurfaceType[];
  readonly message: string;
}

/**
 * 실측 종류마다 둘 수 있는 서피스 타입과 어긋났을 때의 거부 문구 — 실측 종류와 서피스 타입은 1:1 이다
 * (정책 §2-3, ADR-0007 Phase 4 결정). 원통 상품의 면에는 cylinder 만, 평면 상품의 면에는 flat·mesh 만 둔다.
 */
const SURFACE_TYPES_BY_KIND: Readonly<Record<ProductMeasurementKind, SurfaceKindRule>> = {
  FLAT: { allowed: ['flat', 'mesh'], message: '평면 상품에는 원통 서피스를 둘 수 없습니다.' },
  CYLINDER: { allowed: ['cylinder'], message: '원통 상품에는 원통 서피스만 둘 수 있습니다.' },
};

/**
 * 그 실측 종류의 상품에 둘 수 없는 서피스 타입이면 거부 문구, 둘 수 있으면 null.
 * api 저장 검사와 어드민 저장 전 검사가 이 함수 하나를 써서 같은 이유·같은 문구로 막는다.
 */
export const surfaceKindMismatch = (
  type: SurfaceType,
  measurementKind: ProductMeasurementKind,
): string | null => {
  const rule = SURFACE_TYPES_BY_KIND[measurementKind];

  return rule.allowed.includes(type) ? null : rule.message;
};

/**
 * api 원통 쓰기 게이트(EDITOR_CYLINDER_WRITE_ENABLED=false, edit-areas.service)가 cylinder 서피스가 든 정의의 저장을
 * 거부하는 400 문구. 게이트 상태를 읽는 API 는 없어 어드민 툴바는 저장 실패 문구가 이것일 때만 게이트 닫힘을 안내한다(결정 Q12).
 */
export const CYLINDER_WRITE_CLOSED_MESSAGE = '원통 서피스는 아직 저장할 수 없습니다.';

/**
 * 상품에 편집영역 행이 없을 때의 404 문구 — 발행 가드가 막으므로 정상 데이터에서는 생기지 않는다.
 * 편집영역 조회(edit-areas.service)와 디자인 저장 검사(designs.service)가 같은 문구로 거부한다(결정 E1).
 */
export const EDIT_AREA_NOT_FOUND_MESSAGE = '편집영역을 찾을 수 없습니다.';

/**
 * 서피스 실측이 상품 실측 프레임을 따라야 하는 서피스 타입인가. mesh 는 실측 기준이 없어 보지 않고(ADR-0007 D10),
 * 실측 종류와 어긋난 서피스도 보지 않는다 — 그쪽은 surfaceKindMismatch 의 1:1 검사가 먼저 막는다.
 */
export const followsMeasurementFrame = (
  type: SurfaceType,
  measurementKind: ProductMeasurementKind,
): boolean => type !== 'mesh' && surfaceKindMismatch(type, measurementKind) === null;

/**
 * 상품 실측으로 정한 서피스 실측의 기준 프레임. 평면은 실측의 사본이고, 원통은 보이는 반둘레
 * {π × 지름 / 2, 높이}다(ADR-0007 D2, CYLINDER 면 widthMm 가 지름). api 저장 검사와 어드민 실측 갱신이
 * 이 함수 하나를 써서 같은 double 이 나온다 — 반올림하지 않는다.
 */
export const measurementFrameOf = (
  measurementKind: ProductMeasurementKind,
  measurement: SizeMm,
): SizeMm =>
  measurementKind === 'CYLINDER'
    ? { widthMm: cylinderFrameWidthMm(measurement.widthMm), heightMm: measurement.heightMm }
    : { widthMm: measurement.widthMm, heightMm: measurement.heightMm };

/** 실측 drift 검사가 보는 면의 최소 모양 — 저장 정의·응답 정의·어드민 초안의 faces 가 모두 맞는다. */
export type MeasurementFrameFaces = Partial<
  Record<
    FaceKey,
    { readonly surfaces: ReadonlyArray<{ readonly type: SurfaceType; readonly realSize: SizeMm }> }
  >
>;

/**
 * 두 mm 값이 저장 검사 허용오차(GEOMETRY_TOLERANCE) 안에서 같은가. drift 판정(driftedRealSize)과 어드민 원통 인스펙터의
 * 프레임 줄(저장 실측 = 상품 실측 프레임이면 지름을 붙인다)이 이 함수 하나로 비교해 같은 값을 같다고 본다.
 */
export const isSameMm = (a: number, b: number): boolean => Math.abs(a - b) <= GEOMETRY_TOLERANCE;

/**
 * 기준 프레임과 어긋난 첫 서피스의 실측을 FACE_KEYS 순서로 찾는다. 없으면 null.
 * 비교 대상은 followsMeasurementFrame 인 서피스이고 부동소수 비교는 GEOMETRY_TOLERANCE 다.
 * api 저장 검사('상품 실측이 변경되었습니다…' 400)와 어드민 drift 안내가 이 함수 하나로 판정한다.
 */
export const driftedRealSize = (
  faces: MeasurementFrameFaces,
  frame: SizeMm,
  measurementKind: ProductMeasurementKind,
): SizeMm | null => {
  for (const face of FACE_KEYS) {
    for (const surface of faces[face]?.surfaces ?? []) {
      if (
        followsMeasurementFrame(surface.type, measurementKind) &&
        (!isSameMm(surface.realSize.widthMm, frame.widthMm) ||
          !isSameMm(surface.realSize.heightMm, frame.heightMm))
      )
        return surface.realSize;
    }
  }

  return null;
};

export const MAX_COMPONENTS = 20;
export const MAX_COMPONENT_NAME_LENGTH = 30;
/** 실측 mm 상한 — 대형 배너 기준. 서피스 realSize 도 같은 상한을 쓴다. */
export const MAX_COMPONENT_MM = 5000;
export const MAX_SURFACES_PER_FACE = 10;
export const MAX_PLACEMENTS_PER_FACE = 10;
/** 편집영역 색상 수 상한 = 상품 색상 옵션 상한. */
export const MAX_EDIT_AREA_COLORS = MAX_COLOR_OPTIONS;
export const MESH_MIN_GRID = 2;
export const MESH_MAX_GRID = 12;

/** 윤곽(운영자가 찍은 제품 경계) 점 개수 한도. */
export const MIN_OUTLINE_POINTS = 3;
export const MAX_OUTLINE_POINTS = 256;

const productIdOfKey = (key: string, pattern: RegExp): string | null => {
  const productId = pattern.exec(key)?.[1];

  return productId === undefined ? null : productId.toLowerCase();
};

/** 면 사진 키의 상품 세그먼트(소문자). 형식이 어긋나면 null. */
export const facePhotoProductIdOf = (photoKey: string): string | null =>
  productIdOfKey(photoKey, FACE_PHOTO_KEY_PATTERN);

/** 합성 설정의 키 필드와 그 키 형식. 저장 입력의 상품 소유 검사가 같은 표를 본다. */
const COMPOSITE_KEY_PATTERNS = {
  maskKey: FACE_MASK_KEY_PATTERN,
  shadingKey: FACE_SHADING_KEY_PATTERN,
  safeAreaKey: FACE_SAFE_AREA_KEY_PATTERN,
} as const;

// ---------------------------------------------------------------------------
// 정의 구성 요소
// ---------------------------------------------------------------------------

const mmSchema = z.number().positive().max(MAX_COMPONENT_MM);

/** 인쇄 영역 모양 점·구멍 한도. 타원은 이 개수의 점으로 근사해 렌더·검사한다. */
export const MIN_SHAPE_POINTS = 3;
export const MAX_SHAPE_POINTS = 256;
export const MAX_SHAPE_HOLES = 8;
/** 구멍 하나의 점 한도 — 바깥 고리보다 작게 둬 곡선 구멍 여럿의 검사 비용을 묶는다. */
export const MAX_SHAPE_HOLE_POINTS = 64;
export const ELLIPSE_SAMPLE_POINTS = 64;

/** 컴포넌트 좌상단 기준 mm 점. 범위 0..MAX_COMPONENT_MM 은 구조 검사, 가로·세로 안인지는 기하 검사 책임. */
export const shapePointSchema = z.strictObject({
  xMm: z.number().min(0).max(MAX_COMPONENT_MM),
  yMm: z.number().min(0).max(MAX_COMPONENT_MM),
});
export type ShapePoint = Readonly<z.infer<typeof shapePointSchema>>;

/**
 * 곡선 손잡이 점(컴포넌트 mm 절대 좌표). 외접 사각형 가장자리까지 부푼 곡선은 제어점이 그 밖(음수 포함)에
 * 놓이므로 범위가 -MAX_COMPONENT_MM..MAX_COMPONENT_MM 이다.
 */
const shapeHandleSchema = z.strictObject({
  xMm: z.number().min(-MAX_COMPONENT_MM).max(MAX_COMPONENT_MM),
  yMm: z.number().min(-MAX_COMPONENT_MM).max(MAX_COMPONENT_MM),
});

/**
 * 자유 다각형 고리(바깥 고리·구멍 고리)의 점. 선택 손잡이(handleIn·handleOut, 컴포넌트 mm 절대 좌표)가 있으면
 * 그 점에 닿는 구간이 3차 베지어가 된다(flattenPolygonRing).
 */
export const shapeVertexSchema = z.strictObject({
  xMm: z.number().min(0).max(MAX_COMPONENT_MM),
  yMm: z.number().min(0).max(MAX_COMPONENT_MM),
  handleIn: shapeHandleSchema.optional(),
  handleOut: shapeHandleSchema.optional(),
});
export type ShapeVertex = Readonly<z.infer<typeof shapeVertexSchema>>;

const shapeRingSchema = z.array(shapeVertexSchema).min(MIN_SHAPE_POINTS).max(MAX_SHAPE_POINTS);
const shapeHoleRingSchema = z
  .array(shapeVertexSchema)
  .min(MIN_SHAPE_POINTS)
  .max(MAX_SHAPE_HOLE_POINTS);

/** 사각형은 shape 생략. 원형은 외접 사각형(widthMm×heightMm)에 내접하는 타원이다. */
export const componentShapeSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('ellipse') }),
  z.strictObject({
    type: z.literal('polygon'),
    points: shapeRingSchema,
    holes: z.array(shapeHoleRingSchema).max(MAX_SHAPE_HOLES).optional(),
  }),
]);
export type ComponentShape = z.infer<typeof componentShapeSchema>;

/** 제품 공통 인쇄 영역 실측(정책 §1 컴포넌트). 색상마다 달리 정의하지 않는다. */
export const areaComponentSchema = z.strictObject({
  /** 버전 간 안정 — 수정 시 복사 전달. 디자인 문서가 참조한다. */
  id: uuidSchema,
  name: z.string().trim().min(1).max(MAX_COMPONENT_NAME_LENGTH),
  widthMm: mmSchema,
  heightMm: mmSchema,
  /** 인쇄 영역 모양. 없으면 사각형. widthMm×heightMm 는 모양의 외접 사각형이다. 기하 검사는 componentShapeIssue. */
  shape: componentShapeSchema.optional(),
});
export type AreaComponent = z.infer<typeof areaComponentSchema>;

const photoPercentSchema = z.number().min(0).max(100);

export const surfacePointSchema = z.strictObject({ x: photoPercentSchema, y: photoPercentSchema });

/** 서피스가 사진에서 대응하는 제품 표면의 실측 mm — 축척의 원천(정책 §1 축척). */
export const surfaceRealSizeSchema = z.strictObject({ widthMm: mmSchema, heightMm: mmSchema });

const gridCountSchema = z.number().int().min(MESH_MIN_GRID).max(MESH_MAX_GRID);

/**
 * 윤곽 — 운영자가 사진 위 제품 경계를 따라 찍은 닫힌 다각형(정책 §1 서피스의 윤곽). flat·mesh·cylinder 공통 선택 필드다.
 * 없는 행(윤곽 도입 전 저장분)도 유효하다.
 */
export const surfaceOutlineSchema = z.strictObject({
  points: z.array(surfacePointSchema).min(MIN_OUTLINE_POINTS).max(MAX_OUTLINE_POINTS),
});
export type SurfaceOutline = z.infer<typeof surfaceOutlineSchema>;

const OUTLINE_SHAPE_MESSAGE =
  '윤곽은 스스로 교차하지 않고 넓이가 0 보다 큰 닫힌 도형이어야 합니다.';

const isValidOutline = (outline: SurfaceOutline): boolean =>
  polygonArea(outline.points) > GEOMETRY_TOLERANCE && isSimplePolygon(outline.points);

/**
 * 정면 평면의 직사각형. 제어점 4개는 TL·TR·BR·BL 순서이고 축 정렬이어야 한다. 기울어진 평면은 mesh 2×2.
 * 윤곽이 있으면 제어점 4개(틀)는 윤곽을 모두 감싸야 한다 — 축척을 제어점으로 계산하기 때문이다. 틀은 윤곽의
 * 외접 사각형과 같을 수도, 실측 비율에 맞춰 한 축이 더 넓을 수도 있다(가로·세로 축척을 하나로 맞춘 추출).
 */
export const flatSurfaceSchema = z
  .strictObject({
    id: uuidSchema,
    type: z.literal('flat'),
    realSize: surfaceRealSizeSchema,
    points: z.tuple([
      surfacePointSchema,
      surfacePointSchema,
      surfacePointSchema,
      surfacePointSchema,
    ]),
    outline: surfaceOutlineSchema.optional(),
  })
  .superRefine((surface, ctx) => {
    if (!isAxisAlignedRect(surface.points)) {
      ctx.addIssue({
        code: 'custom',
        path: ['points'],
        message: 'flat 서피스의 제어점 4개는 TL·TR·BR·BL 순서의 축 정렬 직사각형이어야 합니다.',
      });
      return;
    }
    if (surface.outline === undefined) return;
    if (!isValidOutline(surface.outline)) {
      ctx.addIssue({ code: 'custom', path: ['outline', 'points'], message: OUTLINE_SHAPE_MESSAGE });
      return;
    }
    if (!rectContainsOutline(surface.points, surface.outline.points)) {
      ctx.addIssue({
        code: 'custom',
        path: ['points'],
        message: 'flat 서피스의 제어점 4개는 윤곽을 모두 감싸야 합니다.',
      });
    }
  });
export type FlatSurface = z.infer<typeof flatSurfaceSchema>;

/** columns×rows 제어점 격자. 원통 곡면은 이 격자가 아니라 cylinder 저장 타입이 맡는다(정책 §2-3, ADR-0007 D1). */
export const meshSurfaceSchema = z
  .strictObject({
    id: uuidSchema,
    type: z.literal('mesh'),
    realSize: surfaceRealSizeSchema,
    columns: gridCountSchema,
    rows: gridCountSchema,
    /** row-major(왼쪽 위부터 오른쪽으로, 다음 행). 길이 = columns × rows. */
    points: z.array(surfacePointSchema),
    outline: surfaceOutlineSchema.optional(),
  })
  .superRefine((surface, ctx) => {
    if (surface.outline !== undefined && !isValidOutline(surface.outline)) {
      ctx.addIssue({ code: 'custom', path: ['outline', 'points'], message: OUTLINE_SHAPE_MESSAGE });
    }

    const expected = surface.columns * surface.rows;
    if (surface.points.length !== expected) {
      ctx.addIssue({
        code: 'custom',
        path: ['points'],
        message: `mesh 제어점은 columns×rows(${expected})개여야 합니다.`,
      });
      return;
    }

    const flipped = findFlippedMeshCell(surface.columns, surface.rows, surface.points);
    if (flipped !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['points'],
        message: `mesh 셀 (${flipped.row}, ${flipped.column}) 이 뒤집혔거나 넓이가 0 입니다.`,
      });
    }
  });
export type MeshSurface = z.infer<typeof meshSurfaceSchema>;

/**
 * 원통 곡면(정책 §2-3, ADR-0007 D7). points 는 사진에서 보이는 원통 사각형(TL·TR·BR·BL)이다.
 * 가로 범위는 투영 폭이고, 세로 범위는 가운데 열의 위·아래 경계다.
 * 곡률 두 값은 위·아래 경계 양 끝의 이동량을 영역 높이로 나눈 값이고, 양 끝이 아래로 가면 + 다(마플 부호).
 * realSize 는 사진 한 장에 보이는 반둘레 프레임이다(ADR-0007 D2).
 */
export const cylinderSurfaceSchema = z
  .strictObject({
    id: uuidSchema,
    type: z.literal('cylinder'),
    realSize: surfaceRealSizeSchema,
    points: z.tuple([
      surfacePointSchema,
      surfacePointSchema,
      surfacePointSchema,
      surfacePointSchema,
    ]),
    topCurveRatio: z.number(),
    bottomCurveRatio: z.number(),
    /** 실루엣 참고용 윤곽. mesh 처럼 모양만 검사하고, points 와 외접 사각형 일치는 요구하지 않는다(ADR-0007 D7). */
    outline: surfaceOutlineSchema.optional(),
  })
  .superRefine((surface, ctx) => {
    if (surface.outline !== undefined && !isValidOutline(surface.outline)) {
      ctx.addIssue({ code: 'custom', path: ['outline', 'points'], message: OUTLINE_SHAPE_MESSAGE });
    }
    if (!isAxisAlignedRect(surface.points)) {
      ctx.addIssue({
        code: 'custom',
        path: ['points'],
        message: 'cylinder 서피스의 제어점 4개는 TL·TR·BR·BL 순서의 축 정렬 직사각형이어야 합니다.',
      });
      return;
    }
    if (doCylinderCurvesCross(surface.topCurveRatio, surface.bottomCurveRatio)) {
      ctx.addIssue({
        code: 'custom',
        path: ['bottomCurveRatio'],
        message: '원통 위·아래 곡선이 만나거나 교차합니다.',
      });
      return;
    }
    const extent = cylinderVerticalExtent(
      surface.points,
      surface.topCurveRatio,
      surface.bottomCurveRatio,
    );
    if (extent.top < -GEOMETRY_TOLERANCE) {
      ctx.addIssue({
        code: 'custom',
        path: ['topCurveRatio'],
        message: '원통 위 곡선의 양 끝이 사진 위로 나갑니다.',
      });
    }
    if (extent.bottom > 100 + GEOMETRY_TOLERANCE) {
      ctx.addIssue({
        code: 'custom',
        path: ['bottomCurveRatio'],
        message: '원통 아래 곡선의 양 끝이 사진 아래로 나갑니다.',
      });
    }
  });
export type CylinderSurface = z.infer<typeof cylinderSurfaceSchema>;

export const surfaceSchema = z.discriminatedUnion('type', [
  flatSurfaceSchema,
  meshSurfaceSchema,
  cylinderSurfaceSchema,
]);
export type Surface = z.infer<typeof surfaceSchema>;

/**
 * 가로 하한이 음수인 것은 cylinder 서피스 때문이다 — 원통 위 배치는 프레임과 겹치기만 하면 된다(ADR-0007 D9).
 * flat·mesh 위 배치의 0 이상 규칙은 교차 검사(배치 ⊂ 서피스)가 지킨다.
 */
export const placementPositionSchema = z.strictObject({
  xMm: z.number().min(-MAX_COMPONENT_MM).max(MAX_COMPONENT_MM),
  yMm: z.number().min(0).max(MAX_COMPONENT_MM),
});

/**
 * 컴포넌트를 서피스 위에 올린 것(정책 §1 배치). 위치만 가진다 — 크기 필드는 없고
 * 사진 위 px 크기는 컴포넌트 mm × 그 서피스의 축척이다(정책 §2-2).
 * 같은 면의 배치는 배열 순서대로 그린다 — 0 번이 맨 아래이고, 클릭 판정은 위부터 한다(ADR-0007 D13).
 */
export const placementSchema = z.strictObject({
  componentId: uuidSchema,
  /** 같은 면의 surfaces[] 안에 있어야 한다. */
  surfaceId: uuidSchema,
  position: placementPositionSchema,
});
export type Placement = z.infer<typeof placementSchema>;

const COMPOSITE_PAIRS = [
  { key: 'shadingKey', size: 'shadingSize', label: '음영' },
  { key: 'safeAreaKey', size: 'safeAreaSize', label: '안내선' },
] as const;

/**
 * 면 합성 설정(ADR-0007 D6·D14) — 색상과 무관한 제품 공통 값이다. 마스크는 필수이고 음영·안내선은 선택이다.
 * 키와 크기는 짝으로 둔다. 크기는 그 면의 모든 색상 사진과 같아야 한다(정의 교차 검사).
 * strict 가 아니다: 응답이 덧붙인 표시 URL 을 화면이 되돌려 보내도 벗겨져 통과하게 한다(facePhotoSchema 와 같다).
 */
export const faceCompositeSchema = z
  .object({
    /** 워프한 디자인을 사진 위에 제자리 곱하기로 합성한다(ADR-0007 D8). */
    multiply: z.boolean(),
    maskKey: faceMaskKeySchema,
    maskSize: facePhotoSizeSchema,
    shadingKey: faceShadingKeySchema.optional(),
    shadingSize: facePhotoSizeSchema.optional(),
    safeAreaKey: faceSafeAreaKeySchema.optional(),
    safeAreaSize: facePhotoSizeSchema.optional(),
  })
  .superRefine((composite, ctx) => {
    for (const pair of COMPOSITE_PAIRS) {
      const hasKey = composite[pair.key] !== undefined;
      if (hasKey === (composite[pair.size] !== undefined)) continue;
      ctx.addIssue({
        code: 'custom',
        path: [hasKey ? pair.size : pair.key],
        message: `${pair.label} 키와 크기는 함께 있어야 합니다.`,
      });
    }
  });
export type FaceComposite = z.infer<typeof faceCompositeSchema>;

const COMPOSITE_URL_PAIRS = [
  { key: 'shadingKey', url: 'shadingUrl' },
  { key: 'safeAreaKey', url: 'safeAreaUrl' },
] as const;

/** 응답 전용 — 서버가 합성 에셋 키마다 표시 URL 을 덧붙인다. 저장하지 않는다(resolvedFacePhotoSchema 와 같다). */
export const resolvedFaceCompositeSchema = faceCompositeSchema
  .extend({
    maskUrl: z.url(),
    shadingUrl: z.url().optional(),
    safeAreaUrl: z.url().optional(),
  })
  .superRefine((composite, ctx) => {
    for (const pair of COMPOSITE_URL_PAIRS) {
      if ((composite[pair.key] !== undefined) === (composite[pair.url] !== undefined)) continue;
      ctx.addIssue({
        code: 'custom',
        path: [pair.url],
        message: '합성 에셋 키와 표시 URL 은 함께 있어야 합니다.',
      });
    }
  });
export type ResolvedFaceComposite = z.infer<typeof resolvedFaceCompositeSchema>;

/** 면 하나의 공통 배치 — 모든 색상의 같은 면 배경 이미지가 공유한다(ERD §8-23). */
const faceDefinitionShape = <TComposite extends z.ZodType>(composite: TComposite) =>
  z.strictObject({
    surfaces: z.array(surfaceSchema).max(MAX_SURFACES_PER_FACE),
    placements: z.array(placementSchema).max(MAX_PLACEMENTS_PER_FACE),
    composite: composite.optional(),
  });
export const faceDefinitionSchema = faceDefinitionShape(faceCompositeSchema);
export type FaceDefinition = z.infer<typeof faceDefinitionSchema>;

const facesShape = <TComposite extends z.ZodType>(composite: TComposite) => {
  const face = faceDefinitionShape(composite);

  return z.strictObject({
    front: face,
    back: face.optional(),
    left: face.optional(),
    right: face.optional(),
  });
};
export const facesSchema = facesShape(faceCompositeSchema);

/**
 * 색상별 면 배경 이미지 — 색상마다 다른 것은 이것뿐이다(정책 §2-4).
 * 정의 안에서 유일하게 strict 가 아니다: 응답이 덧붙인 photoUrl(photoKey 의 파생값)을 화면이 그대로 되돌려
 * 보내도 벗겨져 통과하게 해서, 불러온 정의 위에서 고쳐 저장하는 흐름에 변환 단계를 두지 않는다.
 */
export const facePhotoSchema = z.object({
  photoKey: facePhotoKeySchema,
  /** 원본 px. 축척 계산의 원천이라 정의에 스냅샷한다(ERD §8-22). */
  photoSize: facePhotoSizeSchema,
});
export type FacePhoto = z.infer<typeof facePhotoSchema>;

/** 응답 전용 — 비공개 버킷이라 서버가 photoKey 를 표시 URL 로 해석해 덧붙인다. 저장하지 않는다. */
export const resolvedFacePhotoSchema = facePhotoSchema.extend({ photoUrl: z.url() });
export type ResolvedFacePhoto = z.infer<typeof resolvedFacePhotoSchema>;

const colorFacesSchema = <TPhoto extends z.ZodType>(photo: TPhoto) =>
  z.strictObject({
    front: photo,
    back: photo.optional(),
    left: photo.optional(),
    right: photo.optional(),
  });

const colorEntrySchema = <TPhoto extends z.ZodType>(photo: TPhoto) =>
  z.strictObject({
    /** product_color_options.id — FK 없음, 저장 시 서버가 상품 소속을 검사한다. */
    colorOptionId: uuidSchema,
    /** 키 집합이 최상위 faces 와 같아야 한다. */
    faces: colorFacesSchema(photo),
  });

const definitionShape = <TPhoto extends z.ZodType, TComposite extends z.ZodType>(
  photo: TPhoto,
  composite: TComposite,
) =>
  z.strictObject({
    components: z.array(areaComponentSchema).min(1).max(MAX_COMPONENTS),
    faces: facesShape(composite),
    colors: z.array(colorEntrySchema(photo)).min(1).max(MAX_EDIT_AREA_COLORS),
  });

/**
 * 편집영역 정의 — 저장 입력이자 product_edit_areas.definition 의 모양(schema_version 1).
 * 구조 검사 뒤 교차 검사(id 유일·참조 무결성·배치 ⊂ 서피스·배치 ⊂ 윤곽·원통 배치 겹침·색상 면 키 집합·컴포넌트마다 배치 ≥ 1)를 한다.
 */
export const editAreaDefinitionSchema = definitionShape(
  facePhotoSchema,
  faceCompositeSchema,
).superRefine((definition, ctx) => {
  for (const issue of collectDefinitionIssues(definition)) {
    ctx.addIssue({ code: 'custom', path: [...issue.path], message: issue.message });
  }
});
export type EditAreaDefinition = z.infer<typeof editAreaDefinitionSchema>;

/**
 * 응답에 실리는 정의 — 면 사진마다 photoUrl, 합성 에셋 키마다 표시 URL 이 붙는다.
 * 교차 검사는 저장 경로가 이미 끝냈으므로 반복하지 않는다.
 */
export const resolvedEditAreaDefinitionSchema = definitionShape(
  resolvedFacePhotoSchema,
  resolvedFaceCompositeSchema,
);
export type ResolvedEditAreaDefinition = z.infer<typeof resolvedEditAreaDefinitionSchema>;

// ---------------------------------------------------------------------------
// API 입출력
// ---------------------------------------------------------------------------

export const editAreaProductInputSchema = z.strictObject({ productId: uuidSchema });
export type EditAreaProductInput = z.infer<typeof editAreaProductInputSchema>;

/**
 * 저장 입력. 기준 버전(baseVersion)은 받지 않는다 — 나중 저장이 이긴다(LWW, 정책 §2-6).
 * 모든 면 사진·합성 에셋 키의 상품 세그먼트가 productId 와 같아야 한다 — 다른 상품의 파일을 끌어 쓰는 저장을
 * 계약 단계에서 막는다.
 */
export const saveEditAreaInputSchema = z
  .strictObject({
    productId: uuidSchema,
    definition: editAreaDefinitionSchema,
  })
  .superRefine((input, ctx) => {
    const productId = input.productId.toLowerCase();

    for (const faceKey of FACE_KEYS) {
      const composite = input.definition.faces[faceKey]?.composite;
      if (composite === undefined) continue;
      for (const [field, pattern] of Object.entries(COMPOSITE_KEY_PATTERNS)) {
        const key = composite[field as keyof typeof COMPOSITE_KEY_PATTERNS];
        if (key !== undefined && productIdOfKey(key, pattern) !== productId) {
          ctx.addIssue({
            code: 'custom',
            path: ['definition', 'faces', faceKey, 'composite', field],
            message: '이 상품의 합성 에셋이 아닙니다.',
          });
        }
      }
    }

    input.definition.colors.forEach((color, colorIndex) => {
      for (const faceKey of FACE_KEYS) {
        const photo = color.faces[faceKey];
        if (photo !== undefined && facePhotoProductIdOf(photo.photoKey) !== productId) {
          ctx.addIssue({
            code: 'custom',
            path: ['definition', 'colors', colorIndex, 'faces', faceKey, 'photoKey'],
            message: '이 상품의 면 사진이 아닙니다.',
          });
        }
      }
    });
  });
export type SaveEditAreaInput = z.infer<typeof saveEditAreaInputSchema>;

/** 편집영역 버전 행 + 해석된 정의. 공개·어드민 응답이 공유한다. */
export const editAreaSchema = z.object({
  id: z.uuid(),
  productId: z.uuid(),
  version: z.number().int().min(1),
  /** 행의 schema_version 컬럼 값. JSON 본문 안에는 없다. */
  schemaVersion: z.number().int().min(1),
  definition: resolvedEditAreaDefinitionSchema,
  createdAt: z.iso.datetime(),
});
export type EditArea = z.infer<typeof editAreaSchema>;

/**
 * 어드민 응답 — 운영자가 보고 있는 버전(viewing)과 되돌리기·다시 실행 가능 여부.
 * viewing 은 운영자별 커서(Redis)가 가리키는 행이고, 커서가 없으면 최신 행이다.
 * 공개 표면에는 싣지 않는다(products-public 과 같은 원칙).
 */
export const editAreaAdminStateSchema = z.object({
  latestVersion: z.number().int().min(1),
  viewing: editAreaSchema,
  canUndo: z.boolean(),
  canRedo: z.boolean(),
});
export type EditAreaAdminState = z.infer<typeof editAreaAdminStateSchema>;

/** getLatest 응답 — 편집영역 미정의면 null. */
export const latestEditAreaAdminStateSchema = editAreaAdminStateSchema.nullable();
